import { useEffect, useRef, useState } from 'react';
import type { tCloseSettings } from './LeadClose.types';
import { useTranslation } from 'react-i18next';
import { closeErrorCopyKey } from './LeadClose.functions';
import {
  draftError,
  draftFromSettings,
  MAX_OPTION_LABEL,
  settingsPatch,
  type tDraftError,
  type tOptionsDraft,
} from './LeadCloseOptions.functions';
import { useLeadClose } from './useLeadClose.hook';

/** The server's refusals the editor says in its own words. */
const EDITOR_REASONS = [
  'label_too_long',
  'label_taken',
  'list_empty',
  'list_full',
];

/**
 * The options editor's draft and its save, shared by the form's "Editar
 * opciones" and the Settings card (U17). A fresh draft per settings version,
 * so after a settings_conflict the lists are the current ones.
 */
export function useLeadCloseOptionsDraft() {
  const { t } = useTranslation('chat/chat');
  const { settings, saveSettings } = useLeadClose();
  const [draft, setDraft] = useState<tOptionsDraft | null>(null);
  const [error, setError] = useState<tDraftError | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const version = settings.data?.version;

  // The settings the draft was last built from, and whether the next version
  // must replace the draft: our own save (new options get their uuids) or a
  // colleague's change it ran into.
  const baseRef = useRef<tCloseSettings | null>(null);
  const resetRef = useRef(false);
  useEffect(() => {
    const data = settings.data;
    if (!data) return;
    const base = baseRef.current;
    // Another card's save bumps the version too: unsaved edits here stay.
    const unsaved = !!draft && !!base && settingsPatch(base, draft) !== null;
    if (!unsaved || resetRef.current) {
      setDraft(draftFromSettings(data));
      baseRef.current = data;
    }
    resetRef.current = false;
  }, [version]); // eslint-disable-line react-hooks/exhaustive-deps

  const change = (next: tOptionsDraft) => {
    setDraft(next);
    setError(null);
  };

  /** 'saved', 'unchanged' or 'refused' (the sentence is in `error`/`notice`). */
  const save = async (): Promise<'saved' | 'unchanged' | 'refused'> => {
    if (!draft || !settings.data || busy) return 'refused';
    const wrong = draftError(draft);
    setError(wrong);
    setNotice(null);
    if (wrong) return 'refused';
    const body = settingsPatch(settings.data, draft);
    if (!body) return 'unchanged';
    setBusy(true);
    // Set before the request: the new version may render before we resume.
    resetRef.current = true;
    const refused = await saveSettings(body);
    setBusy(false);
    if (!refused) return 'saved';
    // A refusal without a new version keeps the draft as typed.
    if (refused !== 'settings_conflict') resetRef.current = false;
    if (EDITOR_REASONS.includes(refused) || refused === 'settings_conflict')
      setNotice(
        t(`leadClose.settings.errors.${refused}`, { max: MAX_OPTION_LABEL }),
      );
    else setNotice(t(closeErrorCopyKey(refused, 'lost')));
    return 'refused';
  };

  return { draft, change, error, notice, busy, save };
}
